import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Form, Button, Modal, Badge, InputGroup } from 'react-bootstrap'
import Card from '../../../components/Card'
import { useNavigate } from 'react-router-dom'
import { apiHandler } from '../../../api/apihandler';
import { TEACHERS_ROOMS_API, TEACHERS_ROOMS_STUDENTS_API, TEACHERS_ROOMS_STUDENTS_ATTENDANCE_API } from "../../../api/constants";
import Moment from 'moment';
import loader from '../../../assets/images/page-img/page-load-loader.gif'
import NoDataFound from '../../../components/noDataFound';
import Swal from 'sweetalert2'
import { useSelector } from 'react-redux';

const MyAttendancePage = () => {
    const navigate = useNavigate();
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;
    const [students, setStudents] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [scheduleInfo, setScheduleInfo] = useState(null);
    const [hasSchedule, setHasSchedule] = useState(false);
    const [totalStudents, setTotalStudents] = useState(0);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        loadRooms();
    }, []);

    useEffect(() => {
        if (selectedRoom && selectedDate) {
            loadAttendanceData();
        }
    }, [selectedDate, selectedRoom]);

    const loadRooms = async () => {
        try {
            setLoading(true);
            const response = await apiHandler(TEACHERS_ROOMS_API, 'POST', {});
            if (response.data?.response?.status === 200) {
                const roomsData = response.data?.data?.rooms || [];
                setRooms(roomsData);
                // Auto-select first room if available
                if (roomsData.length > 0 && !selectedRoom) {
                    setSelectedRoom(roomsData[0].id);
                }
            } else {
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load rooms',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            console.error('Error loading rooms:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load rooms',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadAttendanceData = async () => {
        if (!selectedRoom) return;
        
        setLoading(true);
        try {
            const requestPayload = {
                room_id: selectedRoom,
                attendance_date: selectedDate.toISOString().split('T')[0]
            };
            // Add optional parameters if available
            if (profileData?.id || profileData?.user_id) {
                requestPayload.teacher_id = profileData?.id || profileData?.user_id;
            }
            if (profileData?.class_id || profileData?.class?.id) {
                requestPayload.class_id = profileData?.class_id || profileData?.class?.id;
            }
            console.log('Loading attendance with payload:', requestPayload);
            
            const response = await apiHandler(TEACHERS_ROOMS_STUDENTS_API, 'POST', requestPayload);
            
            if (response.data?.response?.status === 200) {
                const studentsData = response.data?.data?.students || [];
                console.log('Raw students data from API:', studentsData);
                
                // Check if we have cached attendance data
                const cacheKey = `attendance_${selectedRoom}_${selectedDate.toISOString().split('T')[0]}`;
                const cachedAttendance = localStorage.getItem(cacheKey);
                const attendanceCache = cachedAttendance ? JSON.parse(cachedAttendance) : {};
                console.log('Loaded attendance from localStorage:', cacheKey, attendanceCache);
                
                const mappedStudents = studentsData.map(student => {
                    console.log('Processing student:', student.name, 'Raw attendance_status:', student.attendance_status);
                    
                    // Check if student is on leave: attendance_status = 2 or is_on_leave = true
                    const isOnLeave = student.attendance_status === 2 || student.attendance_status === '2' || student.is_on_leave === true;
                    
                    // Check multiple possible field names for attendance status
                    let attendanceStatus = student.attendance_status || student.attendance || student.status;
                    let attendanceTime = student.attendance_time;
                    let exitTime = student.exit_time;
                    
                    // If API returns null but we have cached data, use it
                    if (attendanceCache[student.id] && !attendanceStatus && !student.attendance_time) {
                        console.log('Using cached attendance for', student.name, attendanceCache[student.id]);
                        attendanceStatus = attendanceCache[student.id].attendance === 'present' || attendanceCache[student.id].attendance === 'late' ? 1 : 0;
                        attendanceTime = attendanceCache[student.id].attendance_time;
                        exitTime = attendanceCache[student.id].exit_time;
                    }

                    // If status is numeric (0 or 1), convert to text
                    if (attendanceStatus === 1 || attendanceStatus === '1') {
                        attendanceStatus = 'present';
                    } else if (attendanceStatus === 0 || attendanceStatus === '0') {
                        attendanceStatus = 'absent';
                    } else if (attendanceStatus === 2 || attendanceStatus === '2') {
                        attendanceStatus = 'on_leave';
                    }

                    // If no status but has attendance_time, it means attendance was marked as present
                    if (!attendanceStatus && student.attendance_time) {
                        attendanceStatus = 'present';
                    }

                    // Default to absent only if no status is provided and not on leave
                    if (!attendanceStatus && !isOnLeave) {
                        attendanceStatus = 'absent';
                    } else if (isOnLeave && !attendanceStatus) {
                        attendanceStatus = 'on_leave';
                    }

                    // Ensure valid status
                    if (!['present', 'late', 'absent', 'on_leave'].includes(attendanceStatus)) {
                        console.log('Invalid attendance status for student:', student.name, 'Status:', attendanceStatus);
                        attendanceStatus = 'absent';
                    }

                    console.log('Final mapped status for', student.name, ':', attendanceStatus);

                    return {
                        id: student.id,
                        name: student.name,
                        rollNumber: `ST${String(student.id).padStart(3, '0')}`,
                        avatar: student.photo || "https://uzone360live.s3.amazonaws.com/noimage.png",
                        attendance: attendanceStatus,
                        attendance_time: attendanceTime,
                        exit_time: exitTime,
                        is_on_leave: isOnLeave
                    };
                });
                
                setStudents(mappedStudents);
                setTotalStudents(response.data?.data?.total || 0);
                setHasSchedule(response.data?.data?.hasSchedule || false);
                setScheduleInfo(response.data?.data?.scheduleInfo || null);
            } else {
                setStudents([]);
                setTotalStudents(0);
            }
        } catch (error) {
            console.error('Error loading attendance:', error);
            setStudents([]);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load students',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const openStudentProfile = (student) => {
        const studentIdentifier = student?.profileSlug || student?.id;
        if (!studentIdentifier) return;
        navigate(`/dashboard/app/friend-profile/${studentIdentifier}`);
    };

    const updateAttendance = (studentId, status) => {
        setStudents(prevStudents =>
            prevStudents.map(student => {
                if (student.id === studentId) {
                    return {
                        ...student,
                        attendance: status,
                        attendance_time: (status === 'present' || status === 'late') ? Moment().format('HH:mm:ss') : student.attendance_time
                    };
                }
                return student;
            })
        );
    };

    const bulkUpdateAttendance = (status) => {
        setStudents(prevStudents =>
            prevStudents.map(student => ({ ...student, attendance: status }))
        );
        setShowBulkModal(false);
    };

    const saveAttendance = async () => {
        if (!selectedRoom) {
            Swal.fire({
                title: 'Warning',
                text: 'Please select a room first',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        if (students.length === 0) {
            Swal.fire({
                title: 'Warning',
                text: 'No students to save attendance for',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            setLoading(true);
            
            // Map students to attendance array with proper status values
            const attendanceArray = students.map(student => ({
                student_id: student.id,
                status: (student.attendance === 'present' || student.attendance === 'late') ? 1 : 0,
                attendance_time: student.attendance_time || Moment().format('HH:mm:ss'),
                exit_time: student.exit_time || null
            }));

            // Validate attendance array
            if (attendanceArray.length === 0) {
                setLoading(false);
                Swal.fire({
                    title: 'Warning',
                    text: 'No students to save attendance for',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return;
            }

            // Prepare payload with fallback values
            const payload = {
                class_id: profileData?.class_id || profileData?.class?.id || 1,
                room_id: selectedRoom,
                teacher_id: profileData?.id || profileData?.user_id || 1,
                attendance_date: selectedDate.toISOString().split('T')[0],
                attendance: attendanceArray
            };

            console.log('Saving attendance with payload:', payload);
            console.log('Profile data:', profileData);

            const response = await apiHandler(TEACHERS_ROOMS_STUDENTS_ATTENDANCE_API, 'POST', payload);

            console.log('API Response:', response);

            if (response.data?.response?.status === 200) {
                // Save to localStorage for persistence across page refresh
                const cacheKey = `attendance_${selectedRoom}_${selectedDate.toISOString().split('T')[0]}`;
                const attendanceCache = {};
                students.forEach(student => {
                    attendanceCache[student.id] = {
                        attendance: student.attendance,
                        attendance_time: student.attendance_time,
                        exit_time: student.exit_time
                    };
                });
                localStorage.setItem(cacheKey, JSON.stringify(attendanceCache));
                console.log('Saved attendance to localStorage:', cacheKey, attendanceCache);

                Swal.fire({
                    title: 'Success!',
                    text: 'Attendance saved successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3498DB',
                    timer: 2000
                });
                
                // Store current state before reload
                const savedAttendanceState = students.map(s => ({
                    id: s.id,
                    attendance: s.attendance,
                    attendance_time: s.attendance_time,
                    exit_time: s.exit_time
                }));
                
                // Add delay to ensure backend has processed the save
                await new Promise(resolve => setTimeout(resolve, 1500));
                
                // Reload attendance data after save
                setLoading(true);
                const reloadPayload = {
                    room_id: selectedRoom,
                    attendance_date: selectedDate.toISOString().split('T')[0]
                };
                // Add optional parameters if available
                if (profileData?.id || profileData?.user_id) {
                    reloadPayload.teacher_id = profileData?.id || profileData?.user_id;
                }
                if (profileData?.class_id || profileData?.class?.id) {
                    reloadPayload.class_id = profileData?.class_id || profileData?.class?.id;
                }
                console.log('Reloading attendance with payload:', reloadPayload);
                const reloadResponse = await apiHandler(TEACHERS_ROOMS_STUDENTS_API, 'POST', reloadPayload);
                
                if (reloadResponse.data?.response?.status === 200) {
                    const studentsData = reloadResponse.data?.data?.students || [];
                    
                    // Check if backend returned attendance data
                    const hasAttendanceData = studentsData.some(s => 
                        s.attendance_status !== null || s.attendance_time !== null
                    );
                    
                    if (hasAttendanceData) {
                        // Backend returned the data, proceed with normal load
                        await loadAttendanceData();
                    } else {
                        console.warn('Backend did not return saved attendance data, maintaining UI state');
                        // Merge saved attendance with student data
                        const mappedStudents = studentsData.map(student => {
                            const savedData = savedAttendanceState.find(s => s.id === student.id);
                            const isOnLeave = student.attendance_status === 2 || student.attendance_status === '2' || student.is_on_leave === true;
                            
                            return {
                                id: student.id,
                                profileSlug: student.slug || student.user_slug || student?.user?.slug || null,
                                name: student.name,
                                rollNumber: `ST${String(student.id).padStart(3, '0')}`,
                                avatar: student.photo || "https://uzone360live.s3.amazonaws.com/noimage.png",
                                attendance: savedData ? savedData.attendance : (isOnLeave ? 'on_leave' : 'absent'),
                                attendance_time: savedData ? savedData.attendance_time : student.attendance_time,
                                exit_time: savedData ? savedData.exit_time : student.exit_time,
                                is_on_leave: isOnLeave
                            };
                        });
                        
                        setStudents(mappedStudents);
                        setTotalStudents(reloadResponse.data?.data?.total || 0);
                        setHasSchedule(reloadResponse.data?.data?.hasSchedule || false);
                        setScheduleInfo(reloadResponse.data?.data?.scheduleInfo || null);
                    }
                }
                setLoading(false);
            } else {
                setLoading(false);
                const errorMessage = response.data?.response?.message || response.data?.message || 'Failed to save attendance';
                console.error('API Error Response:', response.data);
                throw new Error(errorMessage);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error saving attendance:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                data: error.response?.data
            });
            Swal.fire({
                title: 'Error!',
                text: error.message || error.response?.data?.message || 'Failed to save attendance. Please check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#E74C3C'
            });
        }
    };

    const filteredStudents = students.filter(student =>
        student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.rollNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getAttendanceStats = () => {
        const present = students.filter(s => s.attendance === 'present').length;
        const absent = students.filter(s => s.attendance === 'absent').length;
        const late = students.filter(s => s.attendance === 'late').length;
        const total = students.length;
        return { present, absent, late, total };
    };

    const stats = getAttendanceStats();

    const renderDateSelector = () => (
        <Card className="mb-0" style={{
            borderRadius: '0',
            border: 'none',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            background: '#ffffff'
        }}>
            <Card.Body style={{ padding: '20px' }}>
                <h4 style={{
                    flex: 1,
                    textAlign: 'center',
                    margin: '26px 16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#212529'
                }}>
                    Student Attendance
                </h4>
                
                {/* Room Selector */}
                <h5 style={{ 
                    fontWeight: '600', 
                    fontSize: '16px', 
                    marginBottom: '16px',
                    color: '#212529'
                }}>
                    Select Room
                </h5>
                <div style={{ marginBottom: '16px', position: 'relative' }}>
                    <div 
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#3498DB10',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1px solid #3498DB30',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#3498DB',
                            marginRight: '12px'
                        }}>
                            meeting_room
                        </i>
                        <select
                            value={selectedRoom || ''}
                            onChange={(e) => setSelectedRoom(Number(e.target.value))}
                            style={{
                                flex: 1,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: selectedRoom ? '#212529' : '#6c757d',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                cursor: 'pointer',
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                paddingRight: '30px'
                            }}
                        >
                            <option value="">Choose a room...</option>
                            {rooms.map(room => (
                                <option key={room.id} value={room.id}>
                                    {room.room_name}
                                </option>
                            ))}
                        </select>
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#6c757d',
                            pointerEvents: 'none'
                        }}>
                            keyboard_arrow_down
                        </i>
                    </div>
                </div>

                {/* Date Selector */}
                <h5 style={{ 
                    fontWeight: '600', 
                    fontSize: '16px', 
                    marginBottom: '16px',
                    color: '#212529'
                }}>
                    Select Date
                </h5>
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#3498DB10',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #3498DB30',
                        cursor: 'pointer'
                    }}
                    onClick={() => document.getElementById('dateInput').showPicker()}
                >
                    <i className="material-symbols-outlined" style={{
                        fontSize: '20px',
                        color: '#3498DB',
                        marginRight: '12px'
                    }}>
                        calendar_today
                    </i>
                    <span style={{
                        flex: 1,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#212529'
                    }}>
                        {formatDate(selectedDate)}
                    </span>
                    <i className="material-symbols-outlined" style={{
                        fontSize: '20px',
                        color: '#6c757d'
                    }}>
                        keyboard_arrow_down
                    </i>
                    <input
                        id="dateInput"
                        type="date"
                        value={selectedDate.toISOString().split('T')[0]}
                        onChange={(e) => setSelectedDate(new Date(e.target.value))}
                        max={new Date().toISOString().split('T')[0]}
                        style={{
                            position: 'absolute',
                            opacity: 0,
                            pointerEvents: 'none'
                        }}
                    />
                </div>

                {/* Schedule Info */}
                {hasSchedule && scheduleInfo && (
                    <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        background: '#27AE6010',
                        borderRadius: '8px',
                        border: '1px solid #27AE6030'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                            Schedule Time
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#27AE60' }}>
                            {scheduleInfo.start_time} - {scheduleInfo.end_time}
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    const renderStatsCards = () => (
        <Card className="mb-0" style={{
            borderRadius: '0',
            border: 'none',
            background: '#ffffff'
        }}>
            <Card.Body style={{ padding: '20px' }}>
                <Row style={{ gap: '12px', margin: '0' }}>
                    <Col style={{
                        background: 'rgba(39, 174, 96, 0.08)',
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        border: '0.5px solid rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '20px',
                            background: 'rgba(39, 174, 96, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 8px auto'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '22px',
                                color: '#27AE60'
                            }}>
                                check_circle
                            </i>
                        </div>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#212529',
                            marginTop: '4px',
                            marginBottom: '2px'
                        }}>
                            {stats.present}
                        </div>
                        <div style={{
                            fontSize: '11px',
                            color: '#6c757d',
                            fontWeight: '500',
                            opacity: 0.8
                        }}>
                            Present
                        </div>
                    </Col>
                    <Col style={{
                        background: 'rgba(231, 76, 60, 0.08)',
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        border: '0.5px solid rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '20px',
                            background: 'rgba(231, 76, 60, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 8px auto'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '22px',
                                color: '#E74C3C'
                            }}>
                                cancel
                            </i>
                        </div>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#212529',
                            marginTop: '4px',
                            marginBottom: '2px'
                        }}>
                            {stats.absent}
                        </div>
                        <div style={{
                            fontSize: '11px',
                            color: '#6c757d',
                            fontWeight: '500',
                            opacity: 0.8
                        }}>
                            Absent
                        </div>
                    </Col>
                    <Col style={{
                        background: 'rgba(243, 156, 18, 0.08)',
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        border: '0.5px solid rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '20px',
                            background: 'rgba(243, 156, 18, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 8px auto'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '22px',
                                color: '#F39C12'
                            }}>
                                schedule
                            </i>
                        </div>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#212529',
                            marginTop: '4px',
                            marginBottom: '2px'
                        }}>
                            {stats.late}
                        </div>
                        <div style={{
                            fontSize: '11px',
                            color: '#6c757d',
                            fontWeight: '500',
                            opacity: 0.8
                        }}>
                            Late
                        </div>
                    </Col>
                    <Col style={{
                        background: 'rgba(52, 152, 219, 0.08)',
                        borderRadius: '16px',
                        padding: '16px',
                        textAlign: 'center',
                        border: '0.5px solid rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '20px',
                            background: 'rgba(52, 152, 219, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 8px auto'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '22px',
                                color: '#3498DB'
                            }}>
                                group
                            </i>
                        </div>
                        <div style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#212529',
                            marginTop: '4px',
                            marginBottom: '2px'
                        }}>
                            {stats.total}
                        </div>
                        <div style={{
                            fontSize: '11px',
                            color: '#6c757d',
                            fontWeight: '500',
                            opacity: 0.8
                        }}>
                            Total
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );

    const renderSearchBar = () => (
        <div style={{
            background: '#ffffff',
            padding: '20px 20px 12px 20px'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#ffffff',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}>
                <i className="material-symbols-outlined" style={{
                    fontSize: '20px',
                    color: '#6c757d',
                    marginRight: '12px'
                }}>
                    search
                </i>
                <input
                    type="text"
                    placeholder="Search students by name or roll number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        color: '#212529'
                    }}
                />
                {searchQuery && (
                    <button
                        onClick={() => setSearchQuery('')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            marginLeft: '8px'
                        }}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#6c757d'
                        }}>
                            clear
                        </i>
                    </button>
                )}
            </div>
        </div>
    );

    const renderBulkActions = () => (
        <div style={{
            background: '#ffffff',
            padding: '8px 20px 20px 20px'
        }}>
            <h5 style={{ 
                fontWeight: '600', 
                fontSize: '16px', 
                marginBottom: '8px',
                color: '#212529'
            }}>
                Quick Actions
            </h5>
            <Row style={{ gap: '10px', margin: '0' }}>
                <Col>
                    <button
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#27AE60',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '16px 12px',
                            cursor: 'pointer',
                            minHeight: '65px',
                            boxShadow: '0 4px 8px rgba(39, 174, 96, 0.3)'
                        }}
                        onClick={() => bulkUpdateAttendance('present')}
                    >
                        <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '14px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '6px'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '18px',
                                color: '#ffffff'
                            }}>
                                check
                            </i>
                        </div>
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#ffffff',
                            textAlign: 'center'
                        }}>
                            All Present
                        </span>
                    </button>
                </Col>
                <Col>
                    <button
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#E74C3C',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '16px 12px',
                            cursor: 'pointer',
                            minHeight: '65px',
                            boxShadow: '0 4px 8px rgba(231, 76, 60, 0.3)'
                        }}
                        onClick={() => bulkUpdateAttendance('absent')}
                    >
                        <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '14px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '6px'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '18px',
                                color: '#ffffff'
                            }}>
                                close
                            </i>
                        </div>
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#ffffff',
                            textAlign: 'center'
                        }}>
                            All Absent
                        </span>
                    </button>
                </Col>
                <Col>
                    <button
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#F39C12',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '16px 12px',
                            cursor: 'pointer',
                            minHeight: '65px',
                            boxShadow: '0 4px 8px rgba(243, 156, 18, 0.3)'
                        }}
                        onClick={() => bulkUpdateAttendance('late')}
                    >
                        <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '14px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '6px'
                        }}>
                            <i className="material-symbols-outlined" style={{
                                fontSize: '18px',
                                color: '#ffffff'
                            }}>
                                schedule
                            </i>
                        </div>
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#ffffff',
                            textAlign: 'center'
                        }}>
                            All Late
                        </span>
                    </button>
                </Col>
            </Row>
        </div>
    );

    const renderStudentItem = (student, index) => (
        <div key={student.id}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#ffffff',
                padding: '16px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    cursor: 'pointer'
                }}>
                    <img
                        src={student.avatar}
                        alt={student.name}
                        onClick={() => openStudentProfile(student)}
                        title='Open profile'
                        style={{
                            width: '45px',
                            height: '45px',
                            borderRadius: '22.5px',
                            marginRight: '12px',
                            objectFit: 'cover'
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#212529',
                            marginBottom: '2px'
                        }}
                        onClick={() => openStudentProfile(student)}
                        title='Open profile'
                        >
                            {student.name}
                        </div>
                        <div style={{
                            fontSize: '12px',
                            color: '#6c757d'
                        }}>
                            <span onClick={() => openStudentProfile(student)} title='Open profile'>
                                Roll: {student.rollNumber}
                            </span>
                        </div>
                    </div>
                </div>
                <div style={{
                    display: 'flex',
                    gap: '8px'
                }}>
                    <button
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '16px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: student.attendance === 'present' ? 'none' : '1.5px solid #e9ecef',
                            background: student.attendance === 'present' ? '#28a745' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => updateAttendance(student.id, 'present')}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '16px',
                            color: student.attendance === 'present' ? '#ffffff' : '#28a745'
                        }}>
                            check
                        </i>
                    </button>
                    <button
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '16px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: student.attendance === 'late' ? 'none' : '1.5px solid #e9ecef',
                            background: student.attendance === 'late' ? '#fd7e14' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => updateAttendance(student.id, 'late')}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '16px',
                            color: student.attendance === 'late' ? '#ffffff' : '#fd7e14'
                        }}>
                            schedule
                        </i>
                    </button>
                    <button
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '16px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: student.attendance === 'absent' ? 'none' : '1.5px solid #e9ecef',
                            background: student.attendance === 'absent' ? '#dc3545' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => updateAttendance(student.id, 'absent')}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '16px',
                            color: student.attendance === 'absent' ? '#ffffff' : '#dc3545'
                        }}>
                            close
                        </i>
                    </button>
                </div>
            </div>
            {index < filteredStudents.length - 1 && (
                <div style={{
                    height: '1px',
                    background: 'rgba(0, 0, 0, 0.06)',
                    margin: '12px 16px'
                }} />
            )}
        </div>
    );

    const renderStudentsList = () => (
        <div style={{
            background: '#ffffff',
            padding: '8px 20px 20px 20px'
        }}>
            <h5 style={{ 
                fontWeight: '600', 
                fontSize: '16px', 
                marginBottom: '16px',
                color: '#212529'
            }}>
                Students ({filteredStudents.length})
            </h5>
            {!selectedRoom ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6c757d'
                }}>
                    <i className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>
                        meeting_room
                    </i>
                    <p style={{ marginTop: '16px', fontSize: '14px' }}>
                        Please select a room to view students
                    </p>
                </div>
            ) : filteredStudents.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6c757d'
                }}>
                    <i className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>
                        group_off
                    </i>
                    <p style={{ marginTop: '16px', fontSize: '14px' }}>
                        No students found for this room
                    </p>
                </div>
            ) : (
                <div>
                    {filteredStudents.map((student, index) => renderStudentItem(student, index))}
                </div>
            )}
        </div>
    );

    const renderSaveButton = () => (
        <div style={{
            background: '#ffffff',
            padding: '20px'
        }}>
            <button
                onClick={saveAttendance}
                disabled={loading || students.length === 0}
                style={{
                    width: '100%',
                    background: (loading || students.length === 0) ? '#95a5a6' : '#3498DB',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: students.length === 0 ? 'not-allowed' : 'pointer',
                    boxShadow: (loading || students.length === 0) ? 'none' : '0 6px 10px rgba(52, 152, 219, 0.4)',
                    opacity: students.length === 0 ? 0.6 : 1
                }}
            >
                {loading ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                ) : (
                    <>
                        <i className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                            save
                        </i>
                        Save Attendance
                    </>
                )}
            </button>
        </div>
    );

    const renderBulkModal = () => (
        <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} centered>
            <Modal.Body style={{ 
                padding: '20px',
                borderRadius: '16px'
            }}>
                <h5 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#212529',
                    textAlign: 'center',
                    marginBottom: '20px'
                }}>
                    Bulk Actions
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            width: '100%'
                        }}
                        onClick={() => {
                            bulkUpdateAttendance('present');
                            setShowBulkModal(false);
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#28a745'
                        }}>
                            check_circle
                        </i>
                        <span style={{
                            fontSize: '14px',
                            color: '#212529',
                            flex: 1,
                            textAlign: 'left'
                        }}>
                            Mark All Present
                        </span>
                    </button>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            width: '100%'
                        }}
                        onClick={() => {
                            bulkUpdateAttendance('absent');
                            setShowBulkModal(false);
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#dc3545'
                        }}>
                            cancel
                        </i>
                        <span style={{
                            fontSize: '14px',
                            color: '#212529',
                            flex: 1,
                            textAlign: 'left'
                        }}>
                            Mark All Absent
                        </span>
                    </button>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            width: '100%'
                        }}
                        onClick={() => {
                            bulkUpdateAttendance('late');
                            setShowBulkModal(false);
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#fd7e14'
                        }}>
                            schedule
                        </i>
                        <span style={{
                            fontSize: '14px',
                            color: '#212529',
                            flex: 1,
                            textAlign: 'left'
                        }}>
                            Mark All Late
                        </span>
                    </button>
                    <hr style={{ margin: '8px 0', borderColor: '#e9ecef' }} />
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            width: '100%'
                        }}
                        onClick={() => setShowBulkModal(false)}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                        <i className="material-symbols-outlined" style={{
                            fontSize: '20px',
                            color: '#6c757d'
                        }}>
                            close
                        </i>
                        <span style={{
                            fontSize: '14px',
                            color: '#212529',
                            flex: 1,
                            textAlign: 'left'
                        }}>
                            Cancel
                        </span>
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );

    return (
        <>
            <div id='content-page' className='content-page'>
                <Container //fluid style={{ padding: 0 }}
                >
                    <Row>
                        <Col lg={12} className="mx-auto" style={{ padding: 0 }}>
                            {loading ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '400px',
                                    background: '#ffffff'
                                }}>
                                    <img src={loader} alt="loader" style={{ height: "100px" }} />
                                    <p style={{
                                        color: '#6c757d',
                                        fontSize: '14px',
                                        marginTop: '16px'
                                    }}>
                                        Loading attendance...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {renderDateSelector()}
                                    <Card className="mb-2">
                                        <Card.Body className="p-2 d-flex justify-content-end">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => navigate('/dashboard/app/profile')}
                                                className="d-flex align-items-center gap-1"
                                            >
                                                <i className="ri-user-line"></i>
                                                View My Profile
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                    {renderStatsCards()}
                                    {renderSearchBar()}
                                    {renderBulkActions()}
                                    {renderStudentsList()}
                                    {renderSaveButton()}
                                </>
                            )}
                            {renderBulkModal()}
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    )
}

export default MyAttendancePage